import { formatDate } from "@plasma/utils/time";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { type ErrorOption, get, useFormContext } from "react-hook-form";

import { Button } from "./button";
import { Calendar } from "./calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

interface DateInputProps {
  name: string;
  placeholder: string;
}

type FormValues = {
  [key in string]: Date | null;
};

export const DateInput = ({ name, placeholder }: DateInputProps) => {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<FormValues>();

  const value = watch(name);

  const errorRaw = get(errors, name);
  const error: ErrorOption | undefined =
    typeof errorRaw === "object" && errorRaw !== null ? errorRaw : undefined;

  return (
    <div className="flex w-full flex-col gap-1">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            className="align-center text-muted-foreground data-[state=open]:border-foreground flex h-auto w-full flex-row justify-between px-5 py-3 text-left font-normal"
            type="button"
            variant="outline"
          >
            <div className="flex flex-row items-center justify-center gap-2">
              <CalendarIcon className="size-4" />
              {value ? (
                <span className="body text-foreground font-sans font-normal">
                  {formatDate(value, "MMMM D, YYYY")}
                </span>
              ) : (
                <span className="body text-muted-foreground font-sans font-normal">
                  {placeholder}
                </span>
              )}
            </div>
            {value && (
              <X
                className="h-5 w-5"
                onClick={(e) => {
                  e.stopPropagation();
                  setValue(name, null);
                }}
              />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0">
          <Calendar
            initialFocus
            mode="single"
            onSelect={(date) => {
              setValue(name, date ?? null);
            }}
            selected={value ?? undefined}
          />
        </PopoverContent>
      </Popover>
      {error && <p className="body-sm text-destructive">{error.message}</p>}
    </div>
  );
};
